import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { useState, useCallback, useMemo } from "react";
import {
  Pub,
  PubEtablissement,
  PubStats,
  EtablissementFilterValue,
  SignFilterValue,
  SIGN_FILTER_ASSIGNED,
  SIGN_FILTER_GENERAL,
  useGetAllPubs,
  useGetPubStats,
  useUnassignPubFromEtablissement,
  useUnassignPubFromSign,
} from "../../api/pub";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import {
  CentreAutocomplete,
  CentreValue,
} from "../../components/CentreAutocomplete/CentreAutocomplete";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  Box,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  Typography,
  Tooltip,
} from "@mui/material";
import { ActionsTable } from "./ActionsTable/ActionsTable";
import { ModalAddPub } from "./ModalAddPub/ModalAddPub";
import { ModalDeletePub } from "./ModalDeletePub/ModalDeletePub";
import { colors } from "../../styles/colors";
import { Icon } from "../../components/Icon/Icon";
// Les colonnes sont déclarées au niveau module (voir la note sur FilterControls
// plus bas : les redéclarer dans le corps du composant les remonterait à chaque
// frappe). Elles n'ont donc pas accès au hook useTranslation et lisent i18n
// directement — même instance, même langue courante.
import i18n from "../../i18n/i18n";

const columns = [
  { 
    header: "ID", 
    field: "id",
    render: (data: Record<string, any>) => (
      <Box sx={{ 
        fontWeight: 600, 
        color: colors.primary,
        fontSize: '14px' 
      }}>
        #{data.id}
      </Box>
    )
  },
  { 
    header: "Pub", 
    field: "name", 
    render: (data: Record<string, any>) => (
      <Box display="flex" alignItems="center" gap={1.5}>
        <Avatar 
          src={data.main_image || undefined}
          sx={{ 
            width: 40, 
            height: 40,
            backgroundColor: data.main_image ? 'transparent' : colors.primary,
            border: `2px solid ${colors.default}`
          }}
        >
          {!data.main_image && (data.name?.[0] || "P")}
        </Avatar>
        <Box>
          <Box sx={{ fontWeight: 600, fontSize: '14px', color: colors.BLACK }}>
            {data.name || "-"}
          </Box>
          {data.description && (
            <Box sx={{ 
              fontSize: '12px', 
              color: colors.greydark,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {data.description}
            </Box>
          )}
        </Box>
      </Box>
    )
  },
  { 
    header: "Type", 
    field: "pub_type", 
    render: (data: Record<string, any>) => {
      const typeConfig: Record<string, { color: string; icon: string }> = {
        free: { color: colors.greydark, icon: "tag" },
        premium: { color: colors.primary, icon: "star" },
        sponsored: { color: colors.secondary, icon: "trending-up" }
      };
      
      const config = typeConfig[data.pub_type || "free"];
      
      return (
        <Chip 
          icon={<Icon name={config.icon} width="14px" height="14px" color={colors.WHITE} />}
          label={data.pub_type || "free"} 
          size="small"
          sx={{ 
            backgroundColor: config.color,
            color: colors.WHITE,
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: '11px',
            height: '26px',
            '& .MuiChip-icon': {
              marginLeft: '8px',
            }
          }}
        />
      );
    }
  },
  { 
    header: "Location", 
    field: "city", 
    render: (data: Record<string, any>) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Icon name="map-pin" width="14px" height="14px" color={colors.greydark} />
        <span>{data.city || "-"}</span>
      </Box>
    )
  },
  {
    /*
     * DIFFUSION — la colonne qui répond à « pourquoi ma pub n'apparaît pas ».
     *
     * C'est la question la plus fréquente une fois le ciblage géo en place, et
     * jusqu'ici l'opérateur devait ouvrir chaque fiche pour voir si la campagne
     * portait des coordonnées. Trois états, trois rendus distincts :
     *
     *   pas de coordonnées -> « Partout »   (le rayon est ignoré)
     *   radius_km null      -> « Sans limite » (ciblée, mais portée infinie)
     *   radius_km = N       -> « N km »
     *
     * `is_nationwide` vient de l'API : la règle « pas de coordonnées = partout »
     * ne doit pas être redéduite ici, sinon elle existerait en trois exemplaires
     * (API, admin, mobile) avec trois occasions de diverger.
     */
    header: "Diffusion",
    field: "radius_km",
    render: (data: Record<string, any>) => {
      const isNationwide =
        data.is_nationwide ?? (data.latitude == null || data.longitude == null);

      if (isNationwide) {
        return (
          <Tooltip title={i18n.t("pub.nationwideTooltip", "Aucune coordonnée : diffusée à tous les utilisateurs")}>
            <Chip
              icon={<Icon name="globe" width="14px" height="14px" color={colors.WHITE} />}
              label={i18n.t("pub.nationwide", "Partout")}
              size="small"
              sx={{
                backgroundColor: colors.secondary,
                color: colors.WHITE,
                fontSize: "12px",
                fontWeight: 500,
                height: "26px",
                "& .MuiChip-icon": { marginLeft: "6px" },
              }}
            />
          </Tooltip>
        );
      }

      const label =
        data.radius_km == null
          ? i18n.t("pub.radiusUnlimited", "Sans limite")
          : `${data.radius_km} km`;

      return (
        <Tooltip title={i18n.t("pub.radiusTooltip", "Rayon autour des coordonnées de la pub")}>
          <Chip
            icon={<Icon name="map-pin" width="14px" height="14px" color={colors.darkBlue} />}
            label={label}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: colors.lightBlue,
              color: colors.darkBlue,
              borderColor: colors.darkBlue,
              fontSize: "12px",
              fontWeight: 500,
              height: "26px",
              "& .MuiChip-icon": { marginLeft: "6px" },
            }}
          />
        </Tooltip>
      );
    },
  },
  { 
    header: "Contact", 
    field: "phone", 
    render: (data: Record<string, any>) => (
      <Box>
        {data.phone && (
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <Icon name="phone" width="14px" height="14px" color={colors.greydark} />
            <span style={{ fontSize: '13px' }}>{data.phone}</span>
          </Box>
        )}
        {data.email && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Icon name="mail" width="14px" height="14px" color={colors.greydark} />
            <span style={{ fontSize: '12px', color: colors.greydark }}>
              {data.email.length > 20 ? `${data.email.substring(0, 20)}...` : data.email}
            </span>
          </Box>
        )}
        {!data.phone && !data.email && "-"}
      </Box>
    )
  },
  { 
    header: "Status", 
    field: "is_active", 
    render: (data: Record<string, any>) => {
      const isActive = data.is_pub_active;
      return (
        <Chip 
          icon={<Icon 
            name={isActive ? "check-circle" : "x-circle"} 
            width="14px" 
            height="14px" 
            color={colors.WHITE} 
          />}
          label={isActive ? "Active" : "Inactive"} 
          size="small"
          sx={{
            backgroundColor: isActive ? colors.success : colors.greydark,
            color: colors.WHITE,
            fontWeight: 500,
            fontSize: '12px',
            height: '26px',
            '& .MuiChip-icon': {
              marginLeft: '6px',
            }
          }}
        />
      );
    }
  },
  { 
    header: "Rattachement", 
    field: "sign", 
    render: (data: Record<string, any>) => {
      /*
       * RATTACHEMENT — à QUI la campagne est vendue.
       *
       * Trois états, et non deux depuis l'arrivée du rattachement réseau :
       *
       *   centre  -> un centre précis      (`sign`)
       *   network -> toute une enseigne    (`etablissement`)
       *   general -> la plateforme         (aucun rattachement)
       *
       * La portée vient de l'API (`attachment_scope`) et n'est PAS redéduite
       * ici d'un `!data.sign_id`. Ce test-là était la version précédente de
       * cette cellule, et il devient faux dès qu'une enseigne entre en jeu :
       * une campagne vendue à tout un réseau n'a pas de `sign_id` et se serait
       * affichée « General », c'est-à-dire au mauvais annonceur et au mauvais
       * tarif.
       *
       * `sign_id` / `etablissement_id` restent la vérité de repli quand la
       * relation n'a pas pu être chargée (un centre supprimé encore référencé,
       * par exemple) : afficher « General » dans ce cas serait un mensonge.
       */
      const centre = data.sign as { name?: string | null; centre_code?: string | null } | null | undefined;
      const network = data.etablissement as { name?: string | null } | null | undefined;

      const scope =
        data.attachment_scope ??
        (data.sign_id ? "centre" : data.etablissement_id ? "network" : "general");

      if (scope === "general") {
        return (
          <Chip
            label={i18n.t("pub.scopeGeneral", "Plateforme")}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: 'transparent',
              color: colors.greydark,
              borderColor: colors.greydark,
              fontSize: '12px',
              fontWeight: 500,
            }}
          />
        );
      }

      if (scope === "network") {
        const label = network?.name ?? `#${data.etablissement_id}`;

        return (
          <Tooltip
            title={i18n.t(
              "pub.scopeNetworkTooltip",
              "Vendue à l'enseigne : diffusée dans tous les centres de ce réseau"
            )}
          >
            <Chip
              icon={<Icon name="grid" width="14px" height="14px" color={colors.WHITE} />}
              label={label}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: colors.primary,
                color: colors.WHITE,
                fontSize: '12px',
                fontWeight: 500,
                maxWidth: '180px',
                '& .MuiChip-icon': { marginLeft: '6px' },
              }}
            />
          </Tooltip>
        );
      }

      const label = centre?.name ?? `#${data.sign_id}`;

      return (
        <Tooltip title={centre?.centre_code ? `Code centre : ${centre.centre_code}` : ""}>
          <Chip 
            label={label}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: colors.lightBlue,
              color: colors.darkBlue,
              fontSize: '12px',
              fontWeight: 500,
              maxWidth: '160px',
            }}
          />
        </Tooltip>
      );
    }
  },
];

// ============================================================================
// FILTER + SUMMARY SUBCOMPONENTS
//
// DECLARED AT MODULE LEVEL, NOT INSIDE Pubs().
//
// They used to be defined in the render body and used as <FilterControls />.
// A function declared inside a component gets a NEW IDENTITY on every render,
// and React compares element types by identity - so every keystroke in the
// search box and every filter change unmounted the entire filter bar and
// mounted a fresh one. In practice that meant the MUI Select popup closed the
// instant you picked something, all internal state was thrown away, and the
// whole subtree re-rendered from scratch. Hoisting them is the fix; passing
// state down as props is what makes hoisting possible.
// ============================================================================

type StatisticsCardsProps = {
  stats?: PubStats;
  classes: Record<string, string>;
};

const StatCard: React.FC<{
  label: string;
  value: number;
  valueColor: string;
  iconName: string;
  iconColor: string;
  bgColor: string;
  className: string;
}> = ({ label, value, valueColor, iconName, iconColor, bgColor, className }) => (
  <Card className={className}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={valueColor}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: bgColor, width: 48, height: 48 }}>
          <Icon name={iconName} width="24px" height="24px" color={iconColor} />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const StatisticsCards: React.FC<StatisticsCardsProps> = ({ stats, classes }) => {
  const { t } = useTranslation();

  /*
   * Values come from GET /pub/stats, not from the fetched rows.
   *
   * They were computed as `pubs.filter(...).length` over the SAME array the
   * table renders - i.e. over the current filter. Filter to one centre and the
   * card labelled "Total Pubs" reported that centre's count; the moment
   * pagination is switched on it would report the page size. A summary that
   * moves with the filter it sits above is worse than no summary.
   */
  const cards = [
    {
      label: t("pub.statsTotal", "Total Pubs"),
      value: stats?.total ?? 0,
      valueColor: colors.BLACK,
      iconName: "grid",
      iconColor: colors.WHITE,
      bgColor: colors.primary,
    },
    {
      label: t("pub.statsActive", "Active"),
      value: stats?.active ?? 0,
      valueColor: colors.success,
      iconName: "check",
      iconColor: colors.success,
      bgColor: colors.greenLight,
    },
    {
      // Replaces the "Free" card. Free/premium/sponsored is already the Type
      // column and the Type filter; how much of the catalogue is attached to a
      // centre versus running platform-wide is the number this screen exists to
      // answer and it was nowhere on it.
      label: t("pub.statsAssigned", "Assigned to a centre"),
      value: stats?.assigned ?? 0,
      valueColor: colors.darkBlue,
      iconName: "map-pin",
      iconColor: colors.darkBlue,
      bgColor: colors.lightBlue,
    },
    {
      /*
       * Vendues à une enseigne entière.
       *
       * Remplace la carte « Sponsored », pour la même raison que « Free » avait
       * été remplacée avant elle : free/premium/sponsored est déjà la colonne
       * Type ET le filtre Type, alors que la répartition centre / réseau /
       * plateforme n'était lisible nulle part.
       *
       * Avec la carte « Plateforme » ci-dessous, l'identité devient vérifiable
       * à l'œil : rattachées + réseau + plateforme = total.
       */
      label: t("pub.statsNetwork", "Vendues à une enseigne"),
      value: stats?.network ?? 0,
      valueColor: colors.primary,
      iconName: "grid",
      iconColor: colors.primary,
      bgColor: colors.default,
    },
    {
      /*
       * `platform`, PAS `general`.
       *
       * `general` ne teste que `sign_id` côté API et englobe donc les campagnes
       * réseau — volontairement, parce qu'il décrit ce que le flux mobile sert
       * réellement (Pub::scopeGeneral). L'utiliser ici ferait compter deux fois
       * les mêmes campagnes entre cette carte et celle du dessus.
       */
      label: t("pub.statsPlatform", "Plateforme (aucun rattachement)"),
      value: stats?.platform ?? 0,
      valueColor: colors.greydark,
      iconName: "tag",
      iconColor: colors.greydark,
      bgColor: colors.default,
    },
    {
      /*
       * Combien de campagnes échappent au ciblage.
       *
       * C'est le seul chiffre qui explique, sans ouvrir une fiche, pourquoi une
       * zone reçoit plus de pubs que prévu : une campagne sans coordonnées est
       * servie à tout le monde et consomme la place du commerce local. Le
       * compteur vient de GET /pub/stats, donc il porte sur tout le catalogue
       * et pas sur la page affichée.
       */
      label: t("pub.statsNationwide", "Diffusées partout"),
      value: stats?.nationwide ?? 0,
      valueColor: colors.secondary,
      iconName: "globe",
      iconColor: colors.secondary,
      bgColor: colors.orangeLight,
    },
  ];

  return (
    <Grid container spacing={2} className={classes.statsContainer}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
          <StatCard {...card} className={classes.statCard} />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * The centre filter's four states, expressed AS THE VALUE ITSELF.
 *
 * The filter has four states and the original <Select> could express two. It
 * offered "All Signs", a broken "General Pubs" entry (the page ran
 * `Number("general")` on it, producing NaN, which the API could only answer with
 * an empty list), and one entry per centre. "Attached to SOME centre, I don't
 * care which" - the question an operator asks when auditing coverage - could not
 * be asked at all.
 *
 * The four states are now:
 *
 *   null         -> no constraint ("All ads")
 *   'general'    -> unattached only        (Pub::SIGN_FILTER_GENERAL)
 *   'assigned'   -> attached to any centre (Pub::SIGN_FILTER_ASSIGNED)
 *   <number>     -> that specific centre
 *
 * Held as the raw value rather than as a wrapper object, which removed the
 * `centreOptionToFilter` translation step entirely. That function existed only
 * to unwrap `{kind, id}` back into what the API wanted, and every such mapping
 * layer is somewhere the sentinel strings can be mistyped - which is exactly how
 * `Number("general")` got in.
 *
 * "All" is the CLEARED state (null) rather than a menu entry: an Autocomplete
 * already has a clear button, and offering both would give the same state two
 * spellings.
 */
type CentreFilterValue = CentreValue;

type FilterControlsProps = {
  selectedCentre: CentreFilterValue;
  onCentreChange: (value: CentreFilterValue) => void;
  selectedEtablissement: EtablissementFilterValue | "";
  onEtablissementChange: (event: SelectChangeEvent<string>) => void;
  etablissements: PubEtablissement[];
  selectedPubType: "" | "free" | "premium" | "sponsored";
  onPubTypeChange: (event: SelectChangeEvent<"" | "free" | "premium" | "sponsored">) => void;
  selectedCity: string;
  onCityChange: (event: SelectChangeEvent<string>) => void;
  cities: string[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  classes: Record<string, string>;
};

const FilterControls: React.FC<FilterControlsProps> = ({
  selectedCentre,
  onCentreChange,
  selectedEtablissement,
  onEtablissementChange,
  etablissements,
  selectedPubType,
  onPubTypeChange,
  selectedCity,
  onCityChange,
  cities,
  hasActiveFilters,
  onClearFilters,
  classes,
}) => {
  const { t } = useTranslation();

  return (
    <Box className={classes.filterControls}>
      <Box className={classes.filterGroup}>
        {/*
          * SERVER-SIDE search, not a client-filtered array.
          *
          * This control used to receive every centre in the catalogue as a prop
          * and filter it in JS on each keystroke. That array had to be fetched on
          * every visit to the page - ~340 kB - whether or not the filter was ever
          * touched, and the same array was handed to the "assign to centre"
          * dropdown, where MUI mounted one <MenuItem> per row and froze the tab
          * for seconds.
          *
          * CentreAutocomplete queries the API for the typed term instead, and
          * fetches nothing until the list is opened. It searches name AND
          * centre_code server-side - the code is the identifier printed on the
          * operator's paperwork, and it is what they paste in.
          */}
        <CentreAutocomplete
          size="small"
          className={classes.filterControl}
          value={selectedCentre}
          onChange={onCentreChange}
          label={t("pub.filterByCentre", "Filter by centre")}
          placeholder={t("pub.filterByCentrePlaceholder", "Name or centre code")}
          // The two non-id states of the filter. Absent from the assignment form,
          // where neither can be saved on a pub.
          scopeOptions={[
            {
              value: SIGN_FILTER_ASSIGNED,
              label: t("pub.assignedToAnyCentre", "Assigned to a centre (any)"),
            },
            {
              value: SIGN_FILTER_GENERAL,
              label: t("pub.generalPubs", "General \u2014 no centre"),
            },
          ]}
        />

        {/*
          * FILTRE ENSEIGNE (réseau).
          *
          * Un <Select> plein, là où le filtre centre juste au-dessus est une
          * recherche serveur — et la différence est délibérée. Le catalogue de
          * centres compte des dizaines de milliers de lignes, ce qui figeait
          * l'onglet à l'ouverture du menu ; les enseignes se comptent en
          * dizaines. Une recherche débouncée ici coûterait une requête par
          * frappe pour économiser un menu déroulant de trente entrées.
          *
          * La liste vient de GET /pub/stats et ne contient que les enseignes
          * qui portent au moins une campagne : proposer une option qui ne peut
          * que renvoyer zéro résultat n'aide personne.
          */}
        <FormControl size="small" className={classes.filterControl}>
          <InputLabel id="etablissement-filter-label">
            {t("pub.filterByEtablissement", "Filtrer par enseigne")}
          </InputLabel>
          <Select
            labelId="etablissement-filter-label"
            id="etablissement-filter"
            value={String(selectedEtablissement)}
            label={t("pub.filterByEtablissement", "Filtrer par enseigne")}
            onChange={onEtablissementChange}
          >
            <MenuItem value="">
              <em>{t("pub.allEtablissements", "Toutes les enseignes")}</em>
            </MenuItem>
            <MenuItem value={SIGN_FILTER_ASSIGNED}>
              {t("pub.assignedToAnyEtablissement", "Vendue à une enseigne (toutes)")}
            </MenuItem>
            <MenuItem value={SIGN_FILTER_GENERAL}>
              {t("pub.noEtablissement", "Sans enseigne")}
            </MenuItem>
            {etablissements.map((etablissement) => (
              <MenuItem key={etablissement.id} value={String(etablissement.id)}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon name="grid" width="14px" height="14px" color={colors.greydark} />
                  {etablissement.name ?? `#${etablissement.id}`}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Pub Type Filter */}
        <FormControl size="small" className={classes.filterControl}>
          <InputLabel id="type-filter-label">
            {t("pub.filterByType", "Filter by Type")}
          </InputLabel>
          <Select
            labelId="type-filter-label"
            id="type-filter"
            value={selectedPubType}
            label={t("pub.filterByType", "Filter by Type")}
            onChange={onPubTypeChange}
          >
            <MenuItem value="">
              <em>{t("pub.allTypes", "All Types")}</em>
            </MenuItem>
            <MenuItem value="free">
              <Box display="flex" alignItems="center" gap={1}>
                <Icon name="tag" width="16px" height="16px" color={colors.greydark} />
                Free
              </Box>
            </MenuItem>
            <MenuItem value="premium">
              <Box display="flex" alignItems="center" gap={1}>
                <Icon name="stars" width="16px" height="16px" color={colors.primary} />
                Premium
              </Box>
            </MenuItem>
            <MenuItem value="sponsored">
              <Box display="flex" alignItems="center" gap={1}>
                <Icon name="trending-up" width="16px" height="16px" color={colors.secondary} />
                Sponsored
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* City Filter */}
        <FormControl size="small" className={classes.filterControl}>
          <InputLabel id="city-filter-label">
            {t("pub.filterByCity", "Filter by City")}
          </InputLabel>
          <Select
            labelId="city-filter-label"
            id="city-filter"
            value={selectedCity}
            label={t("pub.filterByCity", "Filter by City")}
            onChange={onCityChange}
          >
            <MenuItem value="">
              <em>{t("pub.allCities", "All Cities")}</em>
            </MenuItem>
            {cities.map((city) => (
              <MenuItem key={city} value={city}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon name="map-pin" width="14px" height="14px" color={colors.greydark} />
                  {city}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Box className={classes.clearFiltersWrapper}>
          <CustomButtonTable
            variant="secondary"
            textVariant="link"
            label={t("common.clearFilters", "Clear Filters")}
            width="fit-content"
            fontWeight="normal"
            textDecoration="none"
            iconName="x"
            padding="8px 16px"
            textTransform="none"
            onClick={onClearFilters}
          />
        </Box>
      )}
    </Box>
  );
};

type Props = {};

export const Pubs: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  
  const initPub: Pub = {
    id: -1,
    name: null,
    description: null,
    phone: null,
    email: null,
    address: null,
    city: null,
    postal_code: null,
    latitude: null,
    longitude: null,
    radius_km: null,
    main_image: null,
    gallery_images: null,
    pub_type: 'free',
    start_date: null,
    end_date: null,
    is_active: true,
    display_order: null,
    sign_id: null,
    etablissement_id: null,
    /*
     * Une pub vierge n'est vendue à personne : « plateforme » est la valeur
     * qui décrit réellement l'objet, comme `is_nationwide: true` juste en
     * dessous décrit réellement une pub sans coordonnées.
     */
    attachment_scope: "general",
    user_id: null,
    is_pub_active: true,
    /*
     * `true` sur une pub vierge, et ce n'est pas arbitraire : sans coordonnées,
     * une campagne est bien diffusée partout. La valeur initiale doit décrire
     * l'objet tel qu'il est, sinon le bandeau du formulaire annoncerait un
     * ciblage que rien ne porte.
     */
    is_nationwide: true,
    created_at: "",
    updated_at: ""
  };

  // Filter states
  const [selectedCentre, setSelectedCentre] = useState<CentreFilterValue>(null);
  /*
   * "" est l'état NON FILTRÉ, et il est distinct de la sentinelle "general"
   * (« sans enseigne »). Les confondre est exactement ce qui faisait se
   * comporter un menu jamais touché comme un filtre actif côté centre — voir la
   * note sur resolveSignFilter dans App\Services\Pub.
   */
  const [selectedEtablissement, setSelectedEtablissement] = useState<
    EtablissementFilterValue | ""
  >("");
  const [selectedPubType, setSelectedPubType] = useState<"" | "free" | "premium" | "sponsored">("");
  const [selectedCity, setSelectedCity] = useState("");

  // Modal states
  const [openModalPub, setOpenModalPub] = useState(false);
  const [openModalDeletePub, setOpenModalDeletePub] = useState(false);
  const [pub, setPub] = useState<Pub>(initPub);

  // null ("All ads") is the absence of a constraint, and `filters` below omits
  // the key entirely rather than sending `sign_id=`. An empty-but-present filter
  // is not the same request as an absent one.
  const signFilter: SignFilterValue | "" = selectedCentre ?? "";

  /*
   * `include_inactive: true` is REQUIRED here.
   *
   * GET /pub now defaults to active-only, because the same endpoint feeds the
   * mobile app and was serving switched-off and expired campaigns to end users
   * (the active() scope was commented out in App\Services\Pub). The back-office
   * is the one caller that must see everything - that is what the Status column
   * is for - so it opts in explicitly.
   */
  const filters = useMemo(
    () => ({
      include_inactive: true,
      ...(signFilter !== "" && { sign_id: signFilter }),
      ...(selectedEtablissement !== "" && { etablissement_id: selectedEtablissement }),
      ...(selectedPubType && { pub_type: selectedPubType }),
      ...(selectedCity && { city: selectedCity }),
    }),
    [signFilter, selectedEtablissement, selectedPubType, selectedCity]
  );

  // API calls
  const { pubs, isPending } = useGetAllPubs(filters);

  /*
   * Stats are fetched WITHOUT the table's filters.
   *
   * The cards describe the catalogue; the table describes the filter. Passing
   * `filters` here would reintroduce exactly the bug this replaced - "Total
   * Pubs: 3" while there are 400 - just with a server round-trip in the middle.
   */
  const { stats } = useGetPubStats();

  const { mutate: unassignPub } = useUnassignPubFromSign();
  const { mutate: unassignPubFromEtablissement } = useUnassignPubFromEtablissement();

  /*
   * NO centre list is fetched on this screen any more.
   *
   * It used to call useGetSignOptions() on mount and hand the result both to the
   * filter and down into <ModalAddPub>. Two problems, and the second is the one
   * operators actually felt:
   *
   *   1. The endpoint was unpaginated, so the entire centre catalogue (~340 kB)
   *      crossed the wire on every visit - including the visits that never
   *      touched a centre control at all.
   *   2. The modal fed that array to a <Select>, and MUI mounts one <MenuItem>
   *      per child when a Select opens. Thousands of DOM nodes built
   *      synchronously on the main thread: the several-second freeze on opening
   *      "Ajouter une publicite".
   *
   * Both controls now search server-side, debounced, and fetch nothing until
   * they are opened. See components/CentreAutocomplete.
   */
  const { t: translate } = useTranslation();

  /*
   * Cities come from the server (GET /pub/stats), NOT from `pubs`.
   *
   * Deriving them from the fetched rows meant the list was already filtered by
   * city, so selecting one collapsed the dropdown to that single entry and there
   * was no way to switch cities without clearing the filter. A filter's options
   * cannot be built from that filter's own results.
   */
  const cities = useMemo(() => stats?.cities ?? [], [stats]);

  /**
   * Les enseignes du filtre, servies par GET /pub/stats.
   *
   * Même raison que `cities` juste au-dessus : construire cette liste à partir
   * de `pubs` la réduirait à la seule enseigne déjà sélectionnée, et il
   * n'existerait plus aucun moyen d'en choisir une autre sans vider le filtre.
   */
  const etablissements: PubEtablissement[] = useMemo(
    () => stats?.etablissements ?? [],
    [stats]
  );

  const onhandleClose = () => {
    setOpenModalPub(false);
    setOpenModalDeletePub(false);
    setPub(initPub);
  };

  const handleEditPub = (pub: Pub) => {
    setOpenModalPub(true);
    setPub(pub);
  };

  const handleDeletePub = (pub: Pub) => {
    setOpenModalDeletePub(true);
    setPub(pub);
  };

  const handleCentreChange = useCallback((value: CentreFilterValue) => {
    setSelectedCentre(value);
  }, []);

  const handleEtablissementChange = useCallback((event: SelectChangeEvent<string>) => {
    const raw = event.target.value;

    if (raw === "") {
      setSelectedEtablissement("");
      return;
    }

    if (raw === SIGN_FILTER_GENERAL || raw === SIGN_FILTER_ASSIGNED) {
      setSelectedEtablissement(raw);
      return;
    }

    /*
     * Number() sur une SENTINELLE produit NaN, et NaN transmis à l'API donne
     * une liste vide sans erreur : c'est le bug qui a vécu des mois sur le
     * filtre centre. Les deux sentinelles sont donc traitées AVANT la
     * conversion, jamais après, et une conversion qui échoue quand même
     * retombe sur « non filtré » plutôt que d'envoyer un jeton illisible.
     */
    const parsed = Number(raw);
    setSelectedEtablissement(Number.isFinite(parsed) ? parsed : "");
  }, []);

  const handlePubTypeChange = useCallback((event: SelectChangeEvent<"" | "free" | "premium" | "sponsored">) => {
    setSelectedPubType(event.target.value as "" | "free" | "premium" | "sponsored");
  }, []);

  const handleCityChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedCity(event.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedCentre(null);
    setSelectedEtablissement("");
    setSelectedPubType("");
    setSelectedCity("");
  }, []);

  // `selectedCentre === null` IS the "All ads" state now, so there is no pseudo-
  // option left to exclude here - the old `kind !== "all"` test was the only
  // thing standing between "cleared" and "filtered to everything".
  const hasActiveFilters = Boolean(
    selectedCentre !== null || selectedEtablissement !== "" || selectedPubType || selectedCity
  );

  /*
   * Row actions.
   *
   * Built inside the component because they close over the handlers; that is
   * fine - `render` returns elements, it is not itself used as a component type,
   * so it does not cause the remount problem that <FilterControls /> did.
   */
  const actionsColumns = [
    {
      header: "Actions",
      field: "",
      render: (data: Record<string, any>) => {
        const pubData: Pub = data as Pub;
        
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <ActionsTable
              handleEditPub={() => handleEditPub(pubData)}
              handleDeletePub={() => handleDeletePub(pubData)}
            />
            {/*
              * Detach from centre, one click.
              *
              * Reaching "General" previously meant opening the edit modal and
              * picking "General Pub (No Sign)" - which silently did nothing,
              * because the form dropped a null sign_id from the request body.
              * Now that POST /pub/{id}/unassign is reachable (the hook pointed at
              * a non-existent `/pubs/{id}/unassign-sign`), expose it directly.
              */}
            {pubData.sign_id ? (
              <Tooltip title={translate("pub.detachFromCentre", "Detach from its centre")}>
                <Box
                  role="button"
                  aria-label={translate("pub.detachFromCentre", "Detach from its centre")}
                  onClick={() => unassignPub(pubData.id)}
                  sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  <Icon name="x-circle" width="18px" height="18px" color={colors.greydark} />
                </Box>
              </Tooltip>
            ) : null}
            {/*
              * Détacher de l'enseigne, un clic.
              *
              * Une action distincte de la précédente, et non un seul bouton
              * « détacher » : les deux rattachements sont indépendants côté API
              * (POST /unassign ne touche pas l'enseigne, et inversement). Un
              * bouton unique aurait dû deviner lequel des deux l'opérateur
              * visait, et il aurait deviné faux la moitié du temps sur une pub
              * qui porte les deux.
              *
              * Les deux ne peuvent coexister que si le centre appartient
              * réellement à l'enseigne (PubController::attachmentConflict), donc
              * voir les deux icônes sur une même ligne est un état légitime.
              */}
            {pubData.etablissement_id ? (
              <Tooltip
                title={translate("pub.detachFromEtablissement", "Détacher de son enseigne")}
              >
                <Box
                  role="button"
                  aria-label={translate(
                    "pub.detachFromEtablissement",
                    "Détacher de son enseigne"
                  )}
                  onClick={() => unassignPubFromEtablissement(pubData.id)}
                  sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  <Icon name="grid" width="18px" height="18px" color={colors.greydark} />
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
  ];

  const columnsWithActions = [...columns, ...actionsColumns];

  const filteredPubs = pubs || [];


  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}

      <ModalAddPub
        open={openModalPub}
        pubInitial={pub}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      
      <ModalDeletePub
        open={openModalDeletePub}
        pubInitial={pub}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />

      <Sidebar
        styles={{
          sidebarRightRoot: classes.pubRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <Box className={classes.contentWrapper}>
          {/* Header Section */}
          <div className={classes.containerHeader}>
            <div className={classes.divIconArrow}>
              <Box className={classes.iconCircle}>
                <Icon name="trending-up" width="28px" height="28px" color={colors.WHITE} />
              </Box>
              <CustomTypography
                variant="h2"
                text={t("pub.pubManagement", "Pub Management")}
                isUppercase
              />
            </div>

            <Box className={classes.addButton}>
              <CustomButtonTable
                variant="secondary"
                textVariant="link"
                label={t("pub.addPub", "Add Pub")}
                width="fit-content"
                fontWeight="bold"
                textDecoration="none"
                iconName="plus"
                padding="10px 20px"
                textTransform="uppercase"
                onClick={() => setOpenModalPub(true)}
              />
            </Box>
          </div>

          {/* Statistics Cards */}
          <StatisticsCards stats={stats} classes={classes} />

          {/* Filters Section */}
          <FilterControls
            selectedCentre={selectedCentre}
            onCentreChange={handleCentreChange}
            selectedEtablissement={selectedEtablissement}
            onEtablissementChange={handleEtablissementChange}
            etablissements={etablissements}
            selectedPubType={selectedPubType}
            onPubTypeChange={handlePubTypeChange}
            selectedCity={selectedCity}
            onCityChange={handleCityChange}
            cities={cities}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            classes={classes}
          />

          {/* Table Section */}
          <Box className={classes.tableContainer}>
            <CustomTable
              columns={columnsWithActions}
              data={filteredPubs}
              isSearchable
              tableMaxHeight="520px"
              titleHeader={t("pub.listOfPubs", "List of Pubs")}
              showPagination={true}
            />
          </Box>
        </Box>
      </Sidebar>
    </>
  );
};
